<template>
    <v-card variant="text" class="mt-4 mx-auto">
        <v-card-text>
            <v-form v-model="form" @submit.prevent="next">
                <p class="text-center text-h5 mb-4">{{ $t('app.Labels.MedInformation').toUpperCase() }}</p>

                <v-row>
                    <v-col cols="12" md="6">
                        <v-select v-model="selectedUnits" :items="unitOptions" item-value="value" item-title="text"
                            :label="$t('app.Fields.SelectUnits')"></v-select>
                    </v-col>
                </v-row>

                <v-row>
                    <!-- Columna izquierda: Peso y Talla -->
                    <v-col cols="12" md="6">
                        <v-card class="pa-4 mb-4">
                            <h4 class="text-subtitle-1 font-weight-medium mb-3">{{ $t('app.Fields.Weight') }}</h4>
                            <v-text-field type="number" v-model="signos.peso"
                                :suffix="selectedUnits === 'metric' ? 'kg' : 'lbs'" :label="$t('app.Fields.Weight')"
                                persistent-hint :hint="$t('app.Hints.Weight')"
                                :rules="[v => !!v || $t('app.Errors.WeightValidation')]"></v-text-field>
                        </v-card>

                        <v-card class="pa-4">
                            <h4 class="text-subtitle-1 font-weight-medium mb-3">{{ $t('app.Fields.Tall') }}</h4>
                            <template v-if="selectedUnits === 'imperial'">
                                <!-- Campo de pies -->
                                <v-text-field type="number" v-model="signos.feet" suffix="ft"
                                    :label="$t('app.Fields.Feet')" persistent-hint :hint="$t('app.Hints.Feet')"
                                    :rules="[
                                        v => (v !== null && v !== undefined && v !== '') || $t('app.Errors.TallValidation'),
                                        v => v >= 0 || $t('app.Errors.TallValidation')
                                    ]"
                                    @keypress="preventDecimal" />

                                <!-- Campo de pulgadas -->
                                <v-text-field type="number" v-model="signos.inches" suffix="in"
                                    :label="$t('app.Fields.Inches')" persistent-hint :hint="$t('app.Hints.Inches')"
                                    :rules="[
                                        v => (v !== null && v !== undefined && v !== '') || $t('app.Errors.TallValidation'),
                                        v => v >= 0 || $t('app.Errors.TallValidation')
                                    ]"
                                    @keypress="preventDecimal" />

                            </template>
                            <template v-else>
                                <v-text-field type="number" v-model="signos.talla" suffix="m"
                                    :label="$t('app.Fields.Tall')" persistent-hint :hint="$t('app.Hints.Tall')"
                                    :rules="[v => !!v || $t('app.Errors.TallValidation')]"></v-text-field>
                            </template>
                        </v-card>
                    </v-col>

                    <!-- Columna derecha: IMC -->
                    <v-col cols="12" md="6">
                        <v-card class="d-flex flex-column mx-auto my-1 pa-4">
                            <div class="d-flex justify-center text-h5" :class="imcClass">
                                {{ $t('app.Labels.BMI') }}
                            </div>
                            <div class="d-flex align-center flex-column">
                                <div class="text-h5" :class="imcClass">{{ imc }}</div>
                            </div>
                            <div class="d-flex align-center flex-column my-2">
                                <div class="text-caption ma-3" :class="imcClass">{{ imcDescription }}</div>

                                <v-row v-if="imcValue >= 35 && imcValue <= 40" class="my-2">
                                    <v-col cols="12">
                                        <div class="text-grey text-caption">{{ $t('app.Fields.IMCFiles1') }}</div>
                                        <v-file-input :label="$t('app.Labels.Attach')" v-model="InformeMedicoObesidad"
                                            multiple
                                            :rules="[v => !!v.length || $t('app.Errors.AtttachDocumentValidation')]"></v-file-input>
                                    </v-col>
                                    <v-col cols="12" class="mt-2">
                                        <div class="text-grey text-caption">{{ $t('app.Fields.IMCFiles2') }}</div>
                                        <v-file-input :label="$t('app.Labels.Attach')"
                                            v-model="ReporteLaboratorioObesidad" multiple
                                            :rules="[v => !!v.length || $t('app.Errors.AtttachDocumentValidation')]"></v-file-input>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Declaraciones -->
                <v-row>
                    <v-col cols="12">
                        <v-checkbox v-model="declareNoCoca" :label="$t('app.Labels.NoCoca')" density="compact"
                            :rules="[v => !!v || $t('app.Errors.DeclarationRequired')]"></v-checkbox>
                    </v-col>
                    <v-col cols="12">
                        <v-checkbox v-model="declareNoMarijuana" :label="$t('app.Labels.NoMarijuana')" density="compact"
                            :rules="[v => !!v || $t('app.Errors.DeclarationRequired')]"></v-checkbox>
                    </v-col>
                </v-row>

                <!-- Alerta IMC fuera de rango -->
                <v-alert v-if="imcOutOfRange" type="warning" dense>
                    {{ $t('app.Errors.BMIOutOfRange') }}
                </v-alert>

                <!-- Botones -->
                <v-card-actions>
                    <v-btn class="mt-2" color="orange-darken-1" variant="flat" @click="atras()"
                        :text="$t('app.Buttons.Back')"></v-btn>
                    <v-spacer></v-spacer>
                    <v-btn :disabled="!formIsValid" class="mt-2" color="success" variant="flat" type="submit"
                        :text="$t('app.Buttons.Next')"></v-btn>
                </v-card-actions>
            </v-form>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    name: 'App',
    data: () => ({
        form: false,
        unitOptions: [
            { value: 'metric', text: 'kg/m' },
            { value: 'imperial', text: 'lbs/ft+in' }
        ]
    }),
    created() {
        this.setUnitsByLanguage(this.$i18n.locale);
    },
    watch: {
        '$i18n.locale'(newLocale) {
            this.setUnitsByLanguage(newLocale);
        }
    },
    methods: {
        next() {
            if (!this.formIsValid) return;
            this.$store.commit('SET_VENTANA', 5);
        },
        atras() {
            this.$store.commit('SET_VENTANA', 3);
        },
        setUnitsByLanguage(locale) {
            this.selectedUnits = locale === 'en' ? 'imperial' : 'metric';
        },
        preventDecimal(event) {
            if (event.key === '.' || event.key === ',') {
                event.preventDefault();
            }
        }
    },
    computed: {
        signos: {
            get() {
                return this.$store.state.signos || { peso: 0, talla: 0, feet: 0, inches: 0 };
            },
            set(newValue) {
                this.$store.commit('SET_SIGNOS', newValue);
            }
        },
        selectedUnits: {
            get() {
                return this.$store.state.selectedUnits;
            },
            set(newValue) {
                this.$store.commit('SET_UNITS', newValue);
            }
        },
        imc() {
            let pesoKg = this.selectedUnits === 'metric'
                ? this.signos.peso
                : this.signos.peso / 2.20462;

            let tallaM;
            if (this.selectedUnits === 'metric') {
                tallaM = this.signos.talla;
            } else {
                const totalInches = (parseFloat(this.signos.feet || 0) * 12) + parseFloat(this.signos.inches || 0);
                tallaM = totalInches * 0.0254;
            }

            if (pesoKg && tallaM) {
                return (pesoKg / (tallaM * tallaM)).toFixed(2);
            }
            return 0;
        },
        imcClass() {
            const imcValue = parseFloat(this.imc);
            if (imcValue >= 40) return 'obesity-grade-3';
            if (imcValue >= 35) return 'obesity-grade-2';
            if (imcValue >= 30) return 'obesity-grade-1';
            return 'normal';
        },
        imcDescription() {
            const imcValue = parseFloat(this.imc);
            if (imcValue >= 40) return this.$t('app.Labels.BMIMessage3');
            if (imcValue >= 35) return this.$t('app.Labels.BMIMessage2');
            if (imcValue >= 30) return this.$t('app.Labels.BMIMessage1');
            return this.$t('app.Labels.BMIMessage1');
        },
        imcValue() {
            return parseFloat(this.imc);
        },
        isIMCValid() {
            const imcValue = this.imcValue;
            return imcValue >= 18 && imcValue <= 60;
        },
        imcOutOfRange() {
            return !this.isIMCValid;
        },
        formIsValid() {
            // Allow advancing even if BMI is out of range
            return this.form && this.declareNoCoca && this.declareNoMarijuana;
        },
        InformeMedicoObesidad: {
            get() {
                return this.$store.state.documentos_adjuntos.InformeMedicoObesidad;
            },
            set(newValue) {
                this.$store.commit('SET_INFORME_MEDICO_OBESIDAD', newValue);
            }
        },
        ReporteLaboratorioObesidad: {
            get() {
                return this.$store.state.documentos_adjuntos.ReporteLaboratorioObesidad;
            },
            set(newValue) {
                this.$store.commit('SET_REPORTE_LABORATORIO_OBESIDAD', newValue);
            }
        },
        declareNoCoca: {
            get() {
                return this.$store.state.declareNoCoca;
            },
            set(newValue) {
                this.$store.commit('SET_DECLARE_NO_COCA', newValue);
            }
        },
        declareNoMarijuana: {
            get() {
                return this.$store.state.declareNoMarijuana;
            },
            set(newValue) {
                this.$store.commit('SET_DECLARE_NO_MARIJUANA', newValue);
            }
        }
    }
}
</script>


<style>
.obesity-grade-1 {
    color: #ffa500;
}

.obesity-grade-2 {
    color: #ff4500;
}

.obesity-grade-3 {
    color: #ff0000;
}

.normal {
    color: #008000;
}
</style>
